import { type DocumentProps, pdf } from '@react-pdf/renderer';
import Cookies from 'js-cookie';
import { type ReactElement } from 'react';
import { STORAGE_PREFIX, COOKIES } from '@avanta/shared/constants/storage';

const COOKIE_HTTP_ONLY = import.meta.env.VITE_COOKIE_HTTP_ONLY === 'true';

export interface StorageUtils {
  getUserId: () => string;
  getRefreshToken: () => string | undefined;
  getAnonymousTokenCookie: () => string | undefined;
  removeAllCookies: () => void;
  setCookieWithExpiry: (key: string, value: string, expiresInSeconds: number) => void;
  setCookieWithoutExpiry: (key: string, value: string) => void;
}

export const createStorageUtils = (storagePrefix: string): StorageUtils => ({
  getUserId: (): string => Cookies.get(`${storagePrefix}${COOKIES.USER_ID}`) || '',
  getRefreshToken: (): string | undefined =>
    Cookies.get(`${storagePrefix}${COOKIES.REFRESH_TOKEN}`),
  getAnonymousTokenCookie: (): string | undefined =>
    Cookies.get(`${storagePrefix}${COOKIES.ANONYMOUS_TOKEN}`),
  removeAllCookies: (): void => {
    Object.values(COOKIES).forEach(cookie => Cookies.remove(`${storagePrefix}${cookie}`));
  },
  setCookieWithExpiry: (key: string, value: string, expiresInSeconds: number): void => {
    Cookies.set(`${storagePrefix}${key}`, value, {
      expires: expiresInSeconds / (60 * 60 * 24),
      httpOnly: COOKIE_HTTP_ONLY,
      secure: true,
      sameSite: 'Strict',
    });
  },
  setCookieWithoutExpiry: (key: string, value: string): void => {
    Cookies.set(`${storagePrefix}${key}`, value, {
      expires: 36500, // ~100 years
      httpOnly: COOKIE_HTTP_ONLY,
      secure: true,
      sameSite: 'Strict',
    });
  },
});

export const openPDF = async ({
  file,
  filename,
  pdfComponent,
}: {
  file?: Blob;
  filename: string;
  pdfComponent?: ReactElement<DocumentProps>;
}): Promise<void> => {
  let blob: Blob | undefined;
  if (pdfComponent) {
    blob = await pdf(pdfComponent).toBlob();
  }
  if (file) {
    blob = file;
  }
  if (!blob) {
    throw new Error('No file provided');
  }
  const url = URL.createObjectURL(blob);
  const newWindow = window.open(url, '_blank');

  if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.pdf`;
    link.click();
  }
};

type DownloadFileParams = {
  content: Blob | string;
  filename: string;
  mimeType?: string;
};

export const downloadFile = ({
  content,
  filename,
  mimeType = 'application/octet-stream',
}: DownloadFileParams): void => {
  const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType });
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
};

export const {
  getUserId,
  getRefreshToken,
  getAnonymousTokenCookie,
  removeAllCookies,
  setCookieWithExpiry,
  setCookieWithoutExpiry,
} = createStorageUtils(STORAGE_PREFIX);
